import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Tabs,
  Divider,
  Typography,
  Row,
  Col,
  Switch,
  InputNumber,
  Popconfirm,
  Empty,
  Tooltip,
  Collapse,
  Alert,
} from "antd";
import {
  SettingOutlined,
  PlusOutlined,
  DeleteOutlined,
  ToolOutlined,
  InboxOutlined,
  ExportOutlined,
  InfoCircleOutlined,
  SaveOutlined,
  ApartmentOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getAllStages, getStageById, updateStage } from "../../api/productionApi";
import { getAllFlows } from "../../api/productionApi";
import PageTitle from "../page-header/PageHeader";

const { Text, Title } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;

// ═══════════════════════════════════════════════════════════════
// PREDEFINED INPUT/OUTPUT TYPES
// ═══════════════════════════════════════════════════════════════
const INPUT_OUTPUT_TYPES = [
  { value: "PAPER_REEL", label: "Paper Reel", icon: "🧻" },
  { value: "CORRUGATED_BOARD", label: "Corrugated Board", icon: "📦" },
  { value: "CUT_SHEETS", label: "Cut Sheets", icon: "📄" },
  { value: "PASTED_SHEETS", label: "Pasted Sheets", icon: "📋" },
  { value: "PRINTED_SHEETS", label: "Printed Sheets", icon: "🖨️" },
  { value: "DIE_CUT_SHEETS", label: "Die-Cut Sheets", icon: "✂️" },
  { value: "FINISHED_BOXES", label: "Finished Boxes", icon: "📦" },
  { value: "SHEETS", label: "Generic Sheets", icon: "📄" },
  { value: "DISPATCHED_GOODS", label: "Dispatched Goods", icon: "🚚" },
];

// ═══════════════════════════════════════════════════════════════
// PAPER ROLES FOR CORRUGATION/PASTING
// ═══════════════════════════════════════════════════════════════
const PAPER_ROLES = [
  { value: "liner", label: "Liner", description: "Flat liner paper" },
  { value: "flute", label: "Flute/Medium", description: "Corrugating medium" },
  { value: "face", label: "Face", description: "Outer face paper" },
  { value: "top", label: "Top Paper", description: "Top pasting paper" },
  { value: "bottom", label: "Bottom Paper", description: "Bottom paper" },
];

// ═══════════════════════════════════════════════════════════════
// CONSUMABLE CATEGORIES
// ═══════════════════════════════════════════════════════════════
const CONSUMABLE_CATEGORIES = [
  { value: "GUM_CORR", label: "Corrugation Gum", color: "green" },
  { value: "GUM_PASTE", label: "Pasting Gum", color: "lime" },
  { value: "INK", label: "Printing Ink", color: "purple" },
  { value: "WIRE", label: "Stitching Wire", color: "cyan" },
  { value: "GLUE", label: "Glueing Adhesive", color: "orange" },
  { value: "SUTLI", label: "Sutli/Twine", color: "gold" },
  { value: "STRAP", label: "Strapping", color: "blue" },
  { value: "TAPE", label: "Tape", color: "magenta" },
];

// ═══════════════════════════════════════════════════════════════
// RATE PER TYPES
// ═══════════════════════════════════════════════════════════════
const RATE_PER_OPTIONS = [
  { value: "100_sqm", label: "per 100 sqm" },
  { value: "100_sqm_per_color", label: "per 100 sqm per color" },
  { value: "box", label: "per box" },
  { value: "sheet", label: "per sheet" },
  { value: "bundle", label: "per bundle" },
  { value: "meter", label: "per meter" },
];

// ═══════════════════════════════════════════════════════════════
// MACHINE SUBTYPES (for "Applies To" field)
// ═══════════════════════════════════════════════════════════════
const MACHINE_SUBTYPES = {
  corrugation: [
    { value: "2_ply", label: "2 Ply" },
    { value: "3_ply", label: "3 Ply" },
    { value: "5_ply", label: "5 Ply" },
    { value: "7_ply", label: "7 Ply" },
  ],
  casting: [
    { value: "die_cutting", label: "Die Cutting (needs die)" },
    { value: "rotary_slotter", label: "Rotary + Slotter (no die)" },
  ],
  pasting: [
    { value: "semi_auto", label: "Semi Auto" },
    { value: "auto", label: "Auto" },
  ],
  printing: [
    { value: "single_color", label: "Single Color" },
    { value: "multi_color", label: "Multi Color" },
  ],
  joining: [
    { value: "stitching", label: "Stitching" },
    { value: "glueing", label: "Glueing" },
    { value: "taping", label: "Taping" },
    { value: "hand_pasting", label: "Hand Pasting" },
  ],
  stitching: [
    { value: "semi_auto", label: "Semi Auto" },
    { value: "auto", label: "Auto" },
  ],
  sheet_cutting: [
    { value: "rotary", label: "Rotary" },
    { value: "flat", label: "Flat" },
  ],
  lamination: [
    { value: "thermal", label: "Thermal" },
    { value: "water_based", label: "Water Based" },
  ],
};

// ═══════════════════════════════════════════════════════════════
// TOOLING TYPES
// ═══════════════════════════════════════════════════════════════
const TOOLING_TYPES = [
  { code: "DIE", name: "Cutting Die", cost_type: "one_time" },
  { code: "CPC_PLATE", name: "CPC Plate", cost_type: "one_time" },
  { code: "FLEXO_PLATE", name: "Flexo Plate", cost_type: "one_time" },
  { code: "SCREEN", name: "Screen/Stencil", cost_type: "one_time" },
  { code: "JIG", name: "Jig/Fixture", cost_type: "one_time" },
  { code: "MOULD", name: "Mould", cost_type: "one_time" },
];

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
const StageConfigManager = () => {
  const [loading, setLoading] = useState(false);
  const [stages, setStages] = useState([]);
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [editingStage, setEditingStage] = useState(null);
  const [configModalVisible, setConfigModalVisible] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, flowsRes] = await Promise.all([
        getAllStages({ is_active: true }),
        getAllFlows(),
      ]);
      setStages(stagesRes);
      setFlows(flowsRes);
      
      const defaultFlow = flowsRes.find(f => f.is_default);
      if (defaultFlow && !selectedFlow) {
        setSelectedFlow(defaultFlow.id);
      }
    } catch (error) {
      toast.error("Failed to load stages");
    } finally {
      setLoading(false);
    }
  }, [selectedFlow]);

  useEffect(() => {
    fetchData();
  }, []);

  const filteredStages = selectedFlow
    ? stages.filter((s) => s.flow_id === selectedFlow)
    : stages;

  const openConfigModal = async (stage) => {
    try {
      const fullStage = await getStageById(stage.id);
      setEditingStage(fullStage);
      setConfigModalVisible(true);
    } catch (error) {
      toast.error("Failed to load stage details");
    }
  };

  const handleSaveConfig = async (updatedStage) => {
    try {
      await updateStage(editingStage.id, updatedStage);
      toast.success("Stage configuration saved");
      setConfigModalVisible(false);
      setEditingStage(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  // Table columns
  const columns = [
    {
      title: "Seq",
      dataIndex: "sequence",
      key: "sequence",
      width: 60,
      render: (seq) => <Tag>{seq}</Tag>,
    },
    {
      title: "Stage",
      dataIndex: "name",
      key: "name",
      render: (name, record) => (
        <Space>
          <span
            style={{
              display: "inline-block",
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: record.color || "#6366F1",
            }}
          />
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>({record.code})</Text>
        </Space>
      ),
    },
    {
      title: "Inputs",
      key: "input_types",
      width: 180,
      render: (_, record) => {
        const inputs = record.input_types || [];
        if (inputs.length === 0) return <Text type="secondary">Not configured</Text>;
        return (
          <Space wrap size={[4, 4]}>
            {inputs.map((input, idx) => (
              <Tag key={idx} color="blue" style={{ margin: 0 }}>
                {INPUT_OUTPUT_TYPES.find(t => t.value === input.type)?.icon} {input.type}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Outputs",
      key: "output_types",
      width: 150,
      render: (_, record) => {
        const outputs = record.output_types || [];
        if (outputs.length === 0) return <Text type="secondary">Not configured</Text>;
        return (
          <Space wrap size={[4, 4]}>
            {outputs.map((output, idx) => (
              <Tag key={idx} color="green" style={{ margin: 0 }}>
                {output.type}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Consumables",
      key: "consumables",
      width: 150,
      render: (_, record) => {
        const consumables = record.consumables || [];
        if (consumables.length === 0) return <Text type="secondary">None</Text>;
        return (
          <Space wrap size={[4, 4]}>
            {consumables.map((c, idx) => (
              <Tooltip key={idx} title={`${c.default_rate} ${c.unit} ${c.rate_per}`}>
                <Tag color="orange" style={{ margin: 0 }}>{c.category}</Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Ply Configs",
      key: "ply_configurations",
      width: 120,
      render: (_, record) => {
        const plyConfig = record.ply_configurations || {};
        const keys = Object.keys(plyConfig);
        if (keys.length === 0) return <Text type="secondary">N/A</Text>;
        return (
          <Tag color="purple">
            {keys.length} variant{keys.length > 1 ? 's' : ''}
          </Tag>
        );
      },
    },
    {
      title: "Tooling",
      key: "tooling",
      width: 100,
      render: (_, record) => {
        const tooling = record.tooling || [];
        if (tooling.length === 0) return <Text type="secondary">None</Text>;
        return (
          <Space wrap size={[4, 4]}>
            {tooling.map((t, idx) => (
              <Tag key={idx} color="red" icon={<ToolOutlined />} style={{ margin: 0 }}>
                {t.code}
              </Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<SettingOutlined />}
          size="small"
          onClick={() => openConfigModal(record)}
        >
          Configure
        </Button>
      ),
    },
  ];

  return (
    <>
      <PageTitle 
        title="Stage Configuration" 
        subtitle="Configure inputs, outputs, consumables, and ply variants for each production stage" 
      />

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Text strong>Production Flow:</Text>
          <Select
            style={{ width: 300 }}
            placeholder="Select a flow"
            value={selectedFlow}
            onChange={setSelectedFlow}
            allowClear
          >
            {flows.map((flow) => (
              <Select.Option key={flow.id} value={flow.id}>
                {flow.name}
                {flow.is_default && <Tag color="blue" style={{ marginLeft: 8 }}>Default</Tag>}
              </Select.Option>
            ))}
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredStages.sort((a, b) => a.sequence - b.sequence)}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          locale={{
            emptyText: (
              <Empty description={selectedFlow ? "No stages in this flow" : "Select a flow"} />
            ),
          }}
        />
      </Card>

      <StageConfigModal
        stage={editingStage}
        visible={configModalVisible}
        onCancel={() => {
          setConfigModalVisible(false);
          setEditingStage(null);
        }}
        onSave={handleSaveConfig}
      />
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// STAGE CONFIGURATION MODAL
// ═══════════════════════════════════════════════════════════════
const StageConfigModal = ({ stage, visible, onCancel, onSave }) => {
  const [inputTypes, setInputTypes] = useState([]);
  const [outputTypes, setOutputTypes] = useState([]);
  const [consumables, setConsumables] = useState([]);
  const [tooling, setTooling] = useState([]);
  const [plyConfigurations, setPlyConfigurations] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (stage) {
      setInputTypes(stage.input_types || []);
      setOutputTypes(stage.output_types || []);
      setConsumables(stage.consumables || []);
      setTooling(stage.tooling || []);
      setPlyConfigurations(stage.ply_configurations || {});
    }
  }, [stage]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        input_types: inputTypes,
        output_types: outputTypes,
        consumables: consumables,
        tooling: tooling,
        ply_configurations: plyConfigurations,
        // Legacy compatibility
        material_inputs: consumables,
        material_outputs: outputTypes,
      });
    } finally {
      setSaving(false);
    }
  };

  if (!stage) return null;

  return (
    <Modal
      title={
        <Space>
          <span style={{ width: 14, height: 14, borderRadius: "50%", background: stage.color || "#6366F1", display: "inline-block" }} />
          <SettingOutlined /> Configure: {stage.name}
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="save" type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          Save Configuration
        </Button>,
      ]}
      destroyOnClose
    >
      <Tabs defaultActiveKey="io">
        {/* ─── Inputs & Outputs Tab ────────────────────────── */}
        <TabPane tab={<span><AppstoreOutlined /> Inputs & Outputs</span>} key="io">
          <Row gutter={24}>
            <Col span={12}>
              <Card size="small" title={<><InboxOutlined /> Input Types</>} style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                  What this stage accepts (e.g., Paper Reels for corrugation)
                </Text>
                {inputTypes.map((input, idx) => (
                  <Card key={idx} size="small" style={{ marginBottom: 8 }}>
                    <Row gutter={8} align="middle">
                      <Col span={10}>
                        <Select
                          value={input.type}
                          onChange={(v) => {
                            const updated = [...inputTypes];
                            updated[idx] = { ...updated[idx], type: v };
                            setInputTypes(updated);
                          }}
                          style={{ width: "100%" }}
                          placeholder="Type"
                        >
                          {INPUT_OUTPUT_TYPES.map((t) => (
                            <Select.Option key={t.value} value={t.value}>
                              {t.icon} {t.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={10}>
                        <Select
                          mode="multiple"
                          value={input.roles || []}
                          onChange={(v) => {
                            const updated = [...inputTypes];
                            updated[idx] = { ...updated[idx], roles: v };
                            setInputTypes(updated);
                          }}
                          style={{ width: "100%" }}
                          placeholder="Roles (optional)"
                          allowClear
                        >
                          {PAPER_ROLES.map((r) => (
                            <Select.Option key={r.value} value={r.value}>{r.label}</Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={4}>
                        <Popconfirm title="Remove?" onConfirm={() => setInputTypes(inputTypes.filter((_, i) => i !== idx))}>
                          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                        </Popconfirm>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setInputTypes([...inputTypes, { type: "", roles: [], description: "" }])}
                  block
                >
                  Add Input Type
                </Button>
              </Card>
            </Col>

            <Col span={12}>
              <Card size="small" title={<><ExportOutlined /> Output Types</>} style={{ marginBottom: 16 }}>
                <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                  What this stage produces
                </Text>
                {outputTypes.map((output, idx) => (
                  <Card key={idx} size="small" style={{ marginBottom: 8 }}>
                    <Row gutter={8} align="middle">
                      <Col span={10}>
                        <Select
                          value={output.type}
                          onChange={(v) => {
                            const updated = [...outputTypes];
                            updated[idx] = { ...updated[idx], type: v };
                            setOutputTypes(updated);
                          }}
                          style={{ width: "100%" }}
                          placeholder="Type"
                        >
                          {INPUT_OUTPUT_TYPES.map((t) => (
                            <Select.Option key={t.value} value={t.value}>
                              {t.icon} {t.label}
                            </Select.Option>
                          ))}
                        </Select>
                      </Col>
                      <Col span={10}>
                        <Input
                          value={output.description}
                          onChange={(e) => {
                            const updated = [...outputTypes];
                            updated[idx] = { ...updated[idx], description: e.target.value };
                            setOutputTypes(updated);
                          }}
                          placeholder="Description"
                        />
                      </Col>
                      <Col span={4}>
                        <Popconfirm title="Remove?" onConfirm={() => setOutputTypes(outputTypes.filter((_, i) => i !== idx))}>
                          <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                        </Popconfirm>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() => setOutputTypes([...outputTypes, { type: "", description: "", unit: "sheets" }])}
                  block
                >
                  Add Output Type
                </Button>
              </Card>
            </Col>
          </Row>
        </TabPane>

        {/* ─── Consumables Tab ─────────────────────────────── */}
        <TabPane tab={<span><InboxOutlined /> Consumables</span>} key="consumables">
          <Alert
            type="info"
            message="Consumables are materials consumed at this stage (gum, ink, wire). Default rates shown here can be overridden by machine-specific rates."
            style={{ marginBottom: 16 }}
            showIcon
          />
          {consumables.map((c, idx) => (
            <Card key={idx} size="small" style={{ marginBottom: 12 }}>
              <Row gutter={12}>
                <Col span={5}>
                  <Form.Item label="Category" style={{ marginBottom: 0 }}>
                    <Select
                      value={c.category}
                      onChange={(v) => {
                        const updated = [...consumables];
                        const cat = CONSUMABLE_CATEGORIES.find(cc => cc.value === v);
                        updated[idx] = { ...updated[idx], category: v, name: cat?.label || v };
                        setConsumables(updated);
                      }}
                    >
                      {CONSUMABLE_CATEGORIES.map((cat) => (
                        <Select.Option key={cat.value} value={cat.value}>
                          <Tag color={cat.color}>{cat.label}</Tag>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Default Rate" style={{ marginBottom: 0 }}>
                    <InputNumber
                      value={c.default_rate}
                      onChange={(v) => {
                        const updated = [...consumables];
                        updated[idx] = { ...updated[idx], default_rate: v };
                        setConsumables(updated);
                      }}
                      style={{ width: "100%" }}
                      min={0}
                      step={0.01}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item label="Unit" style={{ marginBottom: 0 }}>
                    <Select
                      value={c.unit}
                      onChange={(v) => {
                        const updated = [...consumables];
                        updated[idx] = { ...updated[idx], unit: v };
                        setConsumables(updated);
                      }}
                    >
                      <Select.Option value="kg">kg</Select.Option>
                      <Select.Option value="liters">liters</Select.Option>
                      <Select.Option value="meters">meters</Select.Option>
                      <Select.Option value="pieces">pieces</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item label="Rate Per" style={{ marginBottom: 0 }}>
                    <Select
                      value={c.rate_per}
                      onChange={(v) => {
                        const updated = [...consumables];
                        updated[idx] = { ...updated[idx], rate_per: v };
                        setConsumables(updated);
                      }}
                    >
                      {RATE_PER_OPTIONS.map((r) => (
                        <Select.Option key={r.value} value={r.value}>{r.label}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item 
                    label={
                      <Tooltip title="Machine subtype this consumable applies to (optional). Leave empty if applies to all.">
                        Applies To <InfoCircleOutlined style={{ marginLeft: 4 }} />
                      </Tooltip>
                    } 
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      value={c.applies_to}
                      onChange={(v) => {
                        const updated = [...consumables];
                        updated[idx] = { ...updated[idx], applies_to: v };
                        setConsumables(updated);
                      }}
                      placeholder="All subtypes"
                      allowClear
                    >
                      {(MACHINE_SUBTYPES[stage?.code] || MACHINE_SUBTYPES[stage?.machine_type] || []).map((st) => (
                        <Select.Option key={st.value} value={st.value}>{st.label}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={2} style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
                  <Popconfirm title="Remove?" onConfirm={() => setConsumables(consumables.filter((_, i) => i !== idx))}>
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Col>
              </Row>
            </Card>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setConsumables([...consumables, { category: "", name: "", unit: "kg", default_rate: 0, rate_per: "100_sqm" }])}
            block
          >
            Add Consumable
          </Button>
        </TabPane>

        {/* ─── Ply Configurations Tab ──────────────────────── */}
        <TabPane tab={<span><ApartmentOutlined /> Ply Configurations</span>} key="ply">
          <Alert
            type="info"
            message={
              <>
                <strong>Ply configurations</strong> define how many paper inputs are needed for each ply variant.
                <br />
                Example: 3-ply corrugation needs 3 reels (Top Liner + Flute + Bottom Liner).
              </>
            }
            style={{ marginBottom: 16 }}
            showIcon
          />
          
          <PlyConfigEditor
            plyConfigurations={plyConfigurations}
            onChange={setPlyConfigurations}
            stageCode={stage?.code}
          />
        </TabPane>

        {/* ─── Tooling Tab ─────────────────────────────────── */}
        <TabPane tab={<span><ToolOutlined /> Tooling</span>} key="tooling">
          <Alert
            type="info"
            message="Tooling items are non-inventory production assets (dies, plates). They are typically one-time costs per product design."
            style={{ marginBottom: 16 }}
            showIcon
          />
          {tooling.map((t, idx) => (
            <Card key={idx} size="small" style={{ marginBottom: 12 }}>
              <Row gutter={12}>
                <Col span={6}>
                  <Form.Item label="Type" style={{ marginBottom: 0 }}>
                    <Select
                      value={t.code}
                      onChange={(v) => {
                        const updated = [...tooling];
                        const template = TOOLING_TYPES.find(tt => tt.code === v);
                        updated[idx] = { ...updated[idx], code: v, name: template?.name || v, cost_type: template?.cost_type || "one_time" };
                        setTooling(updated);
                      }}
                    >
                      {TOOLING_TYPES.map((tt) => (
                        <Select.Option key={tt.code} value={tt.code}>{tt.name}</Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item label="Display Name" style={{ marginBottom: 0 }}>
                    <Input
                      value={t.name}
                      onChange={(e) => {
                        const updated = [...tooling];
                        updated[idx] = { ...updated[idx], name: e.target.value };
                        setTooling(updated);
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item label="Cost Type" style={{ marginBottom: 0 }}>
                    <Select
                      value={t.cost_type}
                      onChange={(v) => {
                        const updated = [...tooling];
                        updated[idx] = { ...updated[idx], cost_type: v };
                        setTooling(updated);
                      }}
                    >
                      <Select.Option value="one_time">One-Time</Select.Option>
                      <Select.Option value="per_job">Per Job</Select.Option>
                      <Select.Option value="recurring">Recurring</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Required?" style={{ marginBottom: 0 }}>
                    <Switch
                      checked={t.required}
                      onChange={(v) => {
                        const updated = [...tooling];
                        updated[idx] = { ...updated[idx], required: v };
                        setTooling(updated);
                      }}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                  </Form.Item>
                </Col>
                <Col span={3} style={{ display: "flex", alignItems: "flex-end", paddingBottom: 4 }}>
                  <Popconfirm title="Remove?" onConfirm={() => setTooling(tooling.filter((_, i) => i !== idx))}>
                    <Button type="text" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Col>
              </Row>
            </Card>
          ))}
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setTooling([...tooling, { code: "", name: "", cost_type: "one_time", required: false }])}
            block
          >
            Add Tooling
          </Button>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

// ═══════════════════════════════════════════════════════════════
// PLY CONFIGURATION EDITOR
// ═══════════════════════════════════════════════════════════════
const PlyConfigEditor = ({ plyConfigurations, onChange, stageCode }) => {
  const configs = plyConfigurations || {};
  const keys = Object.keys(configs);

  const addPlyConfig = () => {
    const newKey = `variant_${keys.length + 1}`;
    onChange({
      ...configs,
      [newKey]: {
        description: "",
        inputs: [],
        output_ply: 0,
      },
    });
  };

  const updatePlyConfig = (key, field, value) => {
    onChange({
      ...configs,
      [key]: {
        ...configs[key],
        [field]: value,
      },
    });
  };

  const removePlyConfig = (key) => {
    const updated = { ...configs };
    delete updated[key];
    onChange(updated);
  };

  const addInputToConfig = (key) => {
    const current = configs[key]?.inputs || [];
    updatePlyConfig(key, "inputs", [
      ...current,
      { role: "", layer: current.length + 1, paper_type: "", description: "" },
    ]);
  };

  const updateInputInConfig = (key, idx, field, value) => {
    const current = [...(configs[key]?.inputs || [])];
    current[idx] = { ...current[idx], [field]: value };
    updatePlyConfig(key, "inputs", current);
  };

  const removeInputFromConfig = (key, idx) => {
    const current = configs[key]?.inputs || [];
    updatePlyConfig(key, "inputs", current.filter((_, i) => i !== idx));
  };

  const renameKey = (oldKey, newKey) => {
    if (newKey && newKey !== oldKey && !configs[newKey]) {
      const updated = { ...configs };
      updated[newKey] = updated[oldKey];
      delete updated[oldKey];
      onChange(updated);
    }
  };

  return (
    <div>
      <Collapse defaultActiveKey={keys.slice(0, 1)}>
        {keys.map((key) => {
          const config = configs[key];
          return (
            <Panel
              key={key}
              header={
                <Space>
                  <Input
                    value={key}
                    size="small"
                    style={{ width: 120, fontWeight: "bold" }}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={(e) => renameKey(key, e.target.value)}
                    onPressEnter={(e) => renameKey(key, e.target.value)}
                  />
                  <Text type="secondary">{config.description}</Text>
                  <Tag color="blue">{(config.inputs || []).length} inputs</Tag>
                </Space>
              }
              extra={
                <Popconfirm
                  title="Delete this configuration?"
                  onConfirm={(e) => {
                    e.stopPropagation();
                    removePlyConfig(key);
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} size="small" onClick={(e) => e.stopPropagation()} />
                </Popconfirm>
              }
            >
              <Row gutter={16} style={{ marginBottom: 12 }}>
                <Col span={16}>
                  <Form.Item label="Description" style={{ marginBottom: 0 }}>
                    <Input
                      value={config.description}
                      onChange={(e) => updatePlyConfig(key, "description", e.target.value)}
                      placeholder="e.g., 3-Ply Board (Single Wall)"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Output Ply" style={{ marginBottom: 0 }}>
                    <InputNumber
                      value={config.output_ply}
                      onChange={(v) => updatePlyConfig(key, "output_ply", v)}
                      min={0}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ margin: "8px 0" }}>
                Paper Inputs ({(config.inputs || []).length} reels)
              </Divider>

              {(config.inputs || []).map((input, idx) => (
                <Card key={idx} size="small" style={{ marginBottom: 8, background: "#fafafa" }}>
                  <Row gutter={8} align="middle">
                    <Col span={2}>
                      <Text strong>#{input.layer}</Text>
                    </Col>
                    <Col span={5}>
                      <Select
                        value={input.role}
                        onChange={(v) => updateInputInConfig(key, idx, "role", v)}
                        style={{ width: "100%" }}
                        placeholder="Role"
                      >
                        {PAPER_ROLES.map((r) => (
                          <Select.Option key={r.value} value={r.value}>{r.label}</Select.Option>
                        ))}
                      </Select>
                    </Col>
                    <Col span={5}>
                      <Select
                        value={input.paper_type}
                        onChange={(v) => updateInputInConfig(key, idx, "paper_type", v)}
                        style={{ width: "100%" }}
                        placeholder="Paper Type"
                      >
                        <Select.Option value="liner">Liner</Select.Option>
                        <Select.Option value="flute">Flute/Medium</Select.Option>
                        <Select.Option value="top_paper">Top Paper</Select.Option>
                      </Select>
                    </Col>
                    <Col span={9}>
                      <Input
                        value={input.description}
                        onChange={(e) => updateInputInConfig(key, idx, "description", e.target.value)}
                        placeholder="Description (e.g., Top liner)"
                      />
                    </Col>
                    <Col span={3}>
                      <Popconfirm title="Remove?" onConfirm={() => removeInputFromConfig(key, idx)}>
                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                      </Popconfirm>
                    </Col>
                  </Row>
                </Card>
              ))}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => addInputToConfig(key)}
                size="small"
              >
                Add Paper Input
              </Button>
            </Panel>
          );
        })}
      </Collapse>

      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addPlyConfig}
        block
        style={{ marginTop: 12 }}
      >
        Add Ply Configuration
      </Button>
    </div>
  );
};

export default StageConfigManager;
